/**
 * Встречи (DEV-1947, вынесены в свою фичу DEV-1973): общий протокол созвона.
 *
 * Встреча рождается первым приглашением, а не входом в мит-режим: человек,
 * который развернул ленту и пишет один, встречи не начинает. Участник — тот,
 * кто пришёл: приглашение само по себе заметок не открывает.
 *
 * События встречи приезжают потоком NDJSON — той же дорогой, что и ответ
 * ассистента, и по той же причине. Строка потока несёт только вид события,
 * дальше клиент перечитывает ленту: склеенная из кусочков она разошлась бы с
 * серверной, а перечитанная согласована всегда.
 */
import { planApi } from '@/api'
import { ensureFreshToken } from '@/api/http'

import { readNdjson } from '@/features/assistant/stream'

import { toNote, type Note } from './notes'

import type {
  TrackerMeetingInvite,
  TrackerMeetingSession,
  TrackerMeetingState,
} from '@/api/clients/tracker-plan'

const baseURL = import.meta.env.VITE_API_BASE_URL || ''

/**
 * Сколько встреч показываем в списке.
 *
 * Полсотни — это больше квартала созвонов; дальше нужен уже поиск, а не
 * прокрутка, и подгрузка страницами ради этого не окупается.
 */
const MEETINGS_PAGE = 50

/** Участник встречи в том виде, в каком его рисует шапка мит-режима */
export interface MeetingParticipant {
  userId: string
  name: string
  avatarUrl: string
  /** Пришёл ли. Позванный, но не пришедший, заметок не видит */
  joined: boolean
  /**
   * Был ли в звонке на самом деле. У встречи с Meet `joined` стоит у всех
   * приглашённых — это членство, а не присутствие; здесь голый факт, и по нему
   * видно, кого ещё можно убрать из состава (DEV-2129).
   */
  joinedCall: boolean
  /** Откуда взялся: invited, joined_tracker, joined_meet */
  source: string
  /**
   * Есть ли рабочий ящик в домене. Без него приглашение в Google Calendar не
   * придёт — человек узнает о встрече уведомлением трекера и войдёт гостем.
   */
  hasMailbox: boolean
  /** Ответ на приглашение в календаре: accepted, declined, tentative, needsAction */
  responseStatus: string
}

/**
 * Состояние встречи. Зеркало статусов meetings плюс два своих: комнату Meet
 * заводит сосед, и её отсутствие — отдельное состояние экрана, а не ошибка.
 */
export type MeetingStatus =
  | ''
  | 'CREATING'
  | 'CREATE_FAILED'
  | 'SCHEDULED'
  | 'STARTED'
  | 'ENDED'
  | 'PROCESSING'
  | 'READY'
  | 'QUALIFIED'
  | 'NO_RECORDING'
  | 'FAILED'
  | 'CANCELLED'

/** Задача, заведённая на прошлом созвоне серии и до сих пор открытая */
export interface CarryOverIssue {
  key: string
  title: string
  /** Где застряла: колонка доски и её категория */
  columnName: string
  statusCategory: string
}

/**
 * «С прошлой встречи» — то, что осталось с прошлого созвона серии.
 *
 * Только нерешённое: о сделанном напоминать незачем, а закрытая задача в
 * напоминании читается как «сделай ещё раз» (решение 9 гриля).
 */
export interface MeetingCarryOver {
  previousSessionId: string
  /** Конспект прошлого созвона: ссылка «Открыть конспект» ведёт к нему */
  summaryNoteId: string
  actions: Note[]
  openIssues: CarryOverIssue[]
}

export interface MeetingSession {
  id: string
  authorId: string
  /** Завершить встречу может только автор */
  iAmAuthor: boolean
  createdAt: string
  participants: MeetingParticipant[]
  /** Идёт ли она сейчас. Законченная остаётся читаемой — это архив */
  active: boolean
  /** Сколько в ней заметок: список говорит это, не поднимая саму ленту */
  noteCount: number
  /** Тема. Пусто у ад-хок сессий: они начинаются с приглашения, а не с повестки */
  title: string
  /** Когда назначена; пусто у ад-хок */
  scheduledStart: string
  scheduledEnd: string
  /** Когда созвон начался на самом деле; пусто у запланированной */
  startedAt: string
  /** Проект трекера — предзаполняет форму задачи из ИИ-действия */
  projectId: string
  /** Встреча и серия в backend/meetings */
  meetingId: string
  seriesId: string
  /**
   * Первая серия этой повторяющейся встречи.
   *
   * Перенос «эта и следующие» заводит в Google новую серию — со своим
   * идентификатором и своей комнатой. Для человека это по-прежнему один
   * еженедельный созвон, поэтому список группирует по корню, а не по текущей
   * серии (DEV-2178).
   */
  seriesRootId: string
  /** Правило повтора (RRULE) для подписи строки */
  recurrence: string
  /**
   * У серии есть созвон впереди.
   *
   * Считает сервер: по странице списка не отличить «серия закончилась» от
   * «её следующий созвон за пределами страницы» (DEV-2182).
   */
  seriesActive: boolean
  /** Сколько созвонов серии уже прошло — из тех, куда звали */
  seriesPastCount: number
  /** Ключи задач, заведённых из заметок встречи; приезжают в выдаче по серии */
  issueKeys: string[]
  meetingStatus: MeetingStatus
  /** Ссылка на комнату Meet; пусто, пока её заводят */
  joinUrl: string
  /** Почему не завелась комната или сорвалась обработка — текст для человека */
  failureReason: string
  /** Хвост прошлого созвона серии; пусто у первого и у одиночной встречи */
  carryOver: MeetingCarryOver | null
}

/** Состояния, после которых созвона уже не будет */
const DONE_STATUSES: MeetingStatus[] = [
  'ENDED',
  'PROCESSING',
  'READY',
  'QUALIFIED',
  'NO_RECORDING',
  'FAILED',
  'CANCELLED',
]

/**
 * Созвон позади: он не идёт и при этом либо начинался, либо дошёл до
 * терминального состояния, либо его вовсе не назначали — ад-хок встреча
 * начинается приглашением, времени у неё нет, и законченной её делает только
 * собственный конец.
 *
 * Запланированная под это правило не подходит: в неё пишут повестку, и архивом
 * она станет только после разговора.
 */
export const isMeetingDone = (meeting: MeetingSession): boolean =>
  !meeting.active &&
  (!!meeting.startedAt || !meeting.scheduledStart || DONE_STATUSES.includes(meeting.meetingStatus))

/** Планирование встречи, как его собирает форма */
export interface ScheduleMeetingInput {
  title: string
  /** Начало и конец в ISO — то, что уходит на бэкенд */
  start: string
  end: string
  participantIds: string[]
  projectId?: string
  /** Правило повтора (RRULE); пусто — одиночная встреча */
  recurrence?: string
  /** Пояс IANA. Обязателен вместе с повтором: Calendar иначе откажет */
  timeZone?: string
  recordingEnabled: boolean
  /** «Начать сейчас»: созвон идёт с этой минуты */
  startNow?: boolean
  /**
   * Продолжение серии (DEV-2200): новая встреча ложится в её историю. Так
   * возобновляют завершённую серию и заново планируют прошедший отменённый
   * созвон — серия остаётся одной строкой списка, а не двумя.
   */
  seriesRootId?: string
}

/** Область действия переноса и отмены в серии */
export type MeetingScope = 'MEETING_SCOPE_THIS_ONLY' | 'MEETING_SCOPE_THIS_AND_FOLLOWING'

/**
 * Правка запланированной встречи (DEV-2200). Пустые `title` и `recurrence`
 * бэкенд не трогает: форма шлёт только то, что человек поменял.
 */
export interface RescheduleMeetingInput {
  start: string
  end: string
  scope?: MeetingScope
  title?: string
  /** Правило повтора: у одиночной делает её серией, у серии меняет правило */
  recurrence?: string
  timeZone?: string
}

/** Участник для предзаполнения формы: справочника у прошедшей встречи нет */
export interface MeetingFormPerson {
  id: string
  name: string
  hasMailbox: boolean
}

/**
 * Чем предзаполнить форму встречи: правка берёт всё из открытого созвона,
 * «Запланировать снова» — из прошедшего.
 */
export interface MeetingFormPreset {
  title?: string
  start?: Date
  durationMinutes?: number
  projectId?: string
  recurrence?: string
  recordingEnabled?: boolean
  participants?: MeetingFormPerson[]
  seriesRootId?: string
  /** Созвон серии: в правке есть область «только эту / эту и следующие» */
  isSeries?: boolean
  scope?: MeetingScope
}

const WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']

/**
 * Следующая дата по правилу повтора строго после `from`; время суток — из
 * `from`. Только для предзаполнения формы: настоящий календарь считает Google,
 * а здесь нужна разумная дата, которую человек тут же может поправить.
 */
export const nextOccurrence = (recurrence: string, from: Date): Date => {
  const body = recurrence.replace(/^RRULE:/i, '').toUpperCase()
  const next = new Date(from)

  next.setDate(next.getDate() + 1)

  const days = /BYDAY=([A-Z,]+)/.exec(body)?.[1]

  if (!body.includes('FREQ=WEEKLY') || !days) return next

  const wanted = days.split(',').map((day) => WEEKDAYS.indexOf(day)).filter((day) => day >= 0)

  if (!wanted.length) return next

  // Не больше недели вперёд: хоть один из перечисленных дней за неё встретится.
  for (let step = 0; step < 7 && !wanted.includes(next.getDay()); step += 1) {
    next.setDate(next.getDate() + 1)
  }

  return next
}

export interface MeetingInvite {
  sessionId: string
  invitedByName: string
  invitedAt: string
}

export interface MeetingState {
  session: MeetingSession | null
  invites: MeetingInvite[]
}

const toSession = (session: TrackerMeetingSession): MeetingSession => ({
  id: session.id ?? '',
  authorId: session.authorId ?? '',
  iAmAuthor: session.iAmAuthor ?? false,
  createdAt: session.createdAt ?? '',
  participants: (session.participants ?? []).map((participant) => ({
    userId: participant.userId ?? '',
    name: participant.displayName ?? '',
    avatarUrl: participant.avatarUrl ?? '',
    joined: participant.joined ?? false,
    joinedCall: participant.joinedCall ?? false,
    source: participant.source ?? '',
    hasMailbox: participant.hasMailbox ?? false,
    responseStatus: participant.responseStatus ?? '',
  })),
  // Состояние встречи приезжает только списком и карточкой: у баннера его нет,
  // и там она всегда идущая — иначе на неё бы не звали.
  active: session.active ?? true,
  noteCount: session.noteCount ?? 0,
  title: session.title ?? '',
  scheduledStart: session.scheduledStart ?? '',
  scheduledEnd: session.scheduledEnd ?? '',
  startedAt: session.startedAt ?? '',
  projectId: session.projectId ?? '',
  meetingId: session.meetingId ?? '',
  seriesId: session.seriesId ?? '',
  seriesRootId: session.seriesRootId ?? session.seriesId ?? '',
  recurrence: session.recurrence ?? '',
  seriesActive: session.seriesActive ?? false,
  seriesPastCount: session.seriesPastCount ?? 0,
  issueKeys: session.issueKeys ?? [],
  meetingStatus: (session.meetingStatus ?? '') as MeetingStatus,
  joinUrl: session.joinUrl ?? '',
  failureReason: session.failureReason ?? '',
  // Собирается только на открытой встрече: списку он не нужен, а стоит
  // запроса к прошлому созвону и его задачам (DEV-2097).
  carryOver: session.carryOver?.previousSessionId
    ? {
        previousSessionId: session.carryOver.previousSessionId,
        summaryNoteId: session.carryOver.summaryNoteId ?? '',
        actions: (session.carryOver.actions ?? []).map(toNote),
        openIssues: (session.carryOver.openIssues ?? []).map((issue) => ({
          key: issue.key ?? '',
          title: issue.title ?? '',
          columnName: issue.columnName ?? '',
          statusCategory: issue.statusCategory ?? '',
        })),
      }
    : null,
})

const toInvite = (invite: TrackerMeetingInvite): MeetingInvite => ({
  sessionId: invite.sessionId ?? '',
  invitedByName: invite.invitedByName ?? '',
  invitedAt: invite.invitedAt ?? '',
})

const toState = (state: TrackerMeetingState): MeetingState => ({
  session: state.session?.id ? toSession(state.session) : null,
  invites: (state.invites ?? []).map(toInvite),
})

export const getMeetingState = async (): Promise<MeetingState> => {
  const { data } = await planApi.planServiceGetMeetingState()

  return toState(data)
}

export const inviteToMeeting = async (userIds: string[]): Promise<MeetingState> => {
  const { data } = await planApi.planServiceInviteToMeeting({ userIds })

  return toState(data)
}

export const joinMeeting = async (sessionId: string): Promise<MeetingState> => {
  const { data } = await planApi.planServiceJoinMeeting({ sessionId })

  return toState(data)
}

export const endMeeting = async (sessionId: string): Promise<void> => {
  await planApi.planServiceEndMeeting({ sessionId })
}

/**
 * Встречи, где человек был: идущая первой, прошлые за ней (DEV-1971).
 *
 * Заметок список не несёт — их читает лента с идентификатором встречи. Иначе
 * открытие экрана поднимало бы всю историю созвонов разом.
 */
export const listMeetings = async (limit = MEETINGS_PAGE): Promise<MeetingSession[]> => {
  const { data } = await planApi.planServiceListMeetings('1', String(limit))

  return (data.meetings ?? []).map(toSession)
}

/**
 * Созвоны одной повторяющейся встречи — все, включая те, что были до разрыва
 * «эта и следующие»: ими живёт панель записей серии (DEV-2182).
 */
export const listSeriesMeetings = async (
  seriesRootId: string,
  page = 1,
  limit = MEETINGS_PAGE,
): Promise<MeetingSession[]> => {
  const { data } = await planApi.planServiceListMeetings(String(page), String(limit), seriesRootId)

  return (data.meetings ?? []).map(toSession)
}

/**
 * Встречи, назначенные в окне `[from, to)` по времени начала созвона — одна
 * страница для календаря (DEV-2217). Ад-хок сессии без расписания в окно не
 * попадают: у них нет дня, в который их можно положить.
 */
export const listMeetingsBetween = async (
  from: Date,
  to: Date,
  page = 1,
  limit = MEETINGS_PAGE,
): Promise<MeetingSession[]> => {
  const { data } = await planApi.planServiceListMeetings(
    String(page),
    String(limit),
    undefined,
    from.toISOString(),
    to.toISOString(),
  )

  return (data.meetings ?? []).map(toSession)
}

/**
 * Правило повтора человеческим языком: «Каждый вторник, 10:00–10:30».
 *
 * ⚠ В строку списка влезает около 34 знаков — дальше текст обрезается, и
 * первым уходит самое нужное, когда следующий созвон. Поэтому подпись
 * короткая: день недели без «каждый второй», время без пояса (макет 12-20).
 */
export const recurrenceLabel = (
  rule: string,
  start: string,
  t: (key: string, params?: Record<string, unknown>) => string,
): string => {
  if (!rule) return ''

  const body = rule.replace(/^RRULE:/i, '').toUpperCase()

  if (body.includes('FREQ=DAILY')) return t('meetings.series.rule.daily')

  if (body.includes('FREQ=WEEKLY')) {
    const when = start ? new Date(start) : null

    return when
      ? t('meetings.series.rule.weekly', {
          weekday: when.toLocaleDateString('ru', { weekday: 'short' }),
          time: when.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' }),
        })
      : t('meetings.series.rule.custom')
  }

  // Своё правило показываем как есть: разбирать весь RRULE ради подписи
  // строки — работа календаря, а не списка.
  return t('meetings.series.rule.custom')
}

/** Одна встреча, в том числе законченная: её открывают ссылкой из списка */
export const getMeeting = async (sessionId: string): Promise<MeetingSession> => {
  const { data } = await planApi.planServiceGetMeeting(sessionId)

  return toSession(data)
}

/**
 * Запланировать встречу: сессия заметок плюс комната Meet.
 *
 * Сессия появляется первой и живёт даже без комнаты — в неё уже пишут
 * повестку. Отказ Google приходит сюда ошибкой, а сессия остаётся со статусом
 * CREATE_FAILED и кнопкой «Повторить».
 */
export const scheduleMeeting = async (input: ScheduleMeetingInput): Promise<MeetingSession> => {
  const { data } = await planApi.planServiceScheduleMeeting({
    title: input.title,
    scheduledStart: input.start,
    scheduledEnd: input.end,
    participantIds: input.participantIds,
    projectId: input.projectId || undefined,
    recurrence: input.recurrence || undefined,
    timeZone: input.timeZone || undefined,
    recordingEnabled: input.recordingEnabled,
    startNow: input.startNow ?? false,
    seriesRootId: input.seriesRootId || undefined,
  })

  return toSession(data)
}

/** Прикрепить комнату Meet к идущей ад-хок встрече — кнопка «Начать Meet» */
export const attachMeet = async (
  sessionId: string,
  title: string,
  recordingEnabled: boolean,
): Promise<MeetingSession> => {
  const { data } = await planApi.planServiceAttachMeet(sessionId, { title, recordingEnabled })

  return toSession(data)
}

/**
 * Изменить встречу: время, название, правило повтора; для серии — «только
 * эта» или «эта и следующие». Отказ приходит словами бэкенда — у него есть
 * внятные причины («повтор доступен только у встречи с комнатой Meet»).
 */
export const rescheduleMeeting = async (
  sessionId: string,
  input: RescheduleMeetingInput,
): Promise<MeetingSession> => {
  const { data } = await planApi.planServiceRescheduleMeeting(sessionId, {
    scheduledStart: input.start,
    scheduledEnd: input.end,
    scope: input.scope ?? 'MEETING_SCOPE_THIS_ONLY',
    title: input.title || undefined,
    recurrence: input.recurrence || undefined,
    timeZone: input.timeZone || undefined,
  })

  return toSession(data)
}

/** Отменить встречу: событие уходит из календарей, заметки остаются */
export const cancelMeeting = async (
  sessionId: string,
  reason: string,
  scope: MeetingScope = 'MEETING_SCOPE_THIS_ONLY',
): Promise<void> => {
  await planApi.planServiceCancelMeeting(sessionId, { reason, scope })
}

/**
 * Вернуть отменённый созвон на место.
 *
 * Отказ приходит сюда ошибкой и это единственный честный вариант: вернуть
 * можно не всё. Прошедший созвон, закрытую серию и одиночную встречу, событие
 * которой удалено целиком, оживить нечем — заводят заново.
 */
export const restoreMeeting = async (sessionId: string): Promise<MeetingSession> => {
  const { data } = await planApi.planServiceRestoreMeeting(sessionId, {})

  return toSession(data)
}

/**
 * Позвать на встречу ещё раз — всех позванных или только названных: письмо
 * теряется, и чаще всего у одного (DEV-2208)
 */
export const resendMeetingInvites = async (
  sessionId: string,
  userIds: string[] = [],
): Promise<MeetingSession> => {
  const { data } = await planApi.planServiceResendMeetingInvites(
    sessionId,
    userIds.length ? { userIds } : {},
  )

  return toSession(data)
}

/** Завести комнату заново после отказа Google */
export const retryMeetingRoom = async (sessionId: string): Promise<MeetingSession> => {
  const { data } = await planApi.planServiceRetryMeetingRoom(sessionId, {})

  return toSession(data)
}

/**
 * Прогнать запись через разбор заново после сбоя.
 *
 * Чинится ретраем только FAILED — этим он и отличается от NO_RECORDING, где
 * повторять нечего; отказ приходит сюда ошибкой.
 */
export const retryMeetingProcessing = async (sessionId: string): Promise<MeetingSession> => {
  const { data } = await planApi.planServiceRetryMeetingProcessing(sessionId, {})

  return toSession(data)
}

/** Позвать людей в названную встречу: состав уезжает в attendees события */
export const inviteToSession = async (
  sessionId: string,
  userIds: string[],
): Promise<MeetingState> => {
  const { data } = await planApi.planServiceInviteToMeeting({ sessionId, userIds })

  return toState(data)
}

/**
 * Убрать человека из состава встречи.
 *
 * Снимает автор: позвать может любой участник, а выкинуть — только тот, кто
 * встречей распоряжается. Отказ бэкенда объясняет причину словами («уже был на
 * встрече», «писал заметки») — показываем его как есть (DEV-2129).
 */
export const removeFromMeeting = async (
  sessionId: string,
  userId: string,
): Promise<MeetingSession> => {
  const { data } = await planApi.planServiceRemoveFromMeeting(sessionId, userId)

  return toSession(data)
}

/** Реплика расшифровки: говорящий, текст и смещение от начала записи */
export interface TranscriptPhrase {
  speaker: string
  text: string
  offsetMs: number
}

/**
 * Расшифровка созвона. Право `meetings:read_recordings` тут ни при чём — это
 * право CRM про разговор с клиентом; внутренний созвон слушают его участники,
 * и членство проверяет трекер (DEV-2098).
 */
export const getMeetingTranscript = async (sessionId: string): Promise<TranscriptPhrase[]> => {
  const { data } = await planApi.planServiceGetMeetingTranscript(sessionId)

  return (data.phrases ?? []).map((phrase) => ({
    speaker: phrase.speaker ?? '',
    text: phrase.text ?? '',
    offsetMs: Number(phrase.offsetMs ?? 0),
  }))
}

/**
 * Ссылка на аудио созвона — временная, как у записей звонков.
 *
 * Пусто, когда записи нет: её не включили или обработка ещё идёт. Ссылки на
 * mp4 в Google Диске здесь нет и не будет — файл лежит под ACL Google, и
 * участники его не откроют.
 */
export const getMeetingAudioUrl = async (sessionId: string): Promise<string> => {
  const { data } = await planApi.planServiceGetMeetingAudioUrl(sessionId)

  return data.url ?? ''
}

/** Виды событий потока; повторяют `dto.MeetingEventKind` бэкенда */
export type MeetingEventKind =
  | 'open'
  | 'ping'
  | 'note'
  | 'participants'
  | 'ended'
  | 'crdt'
  | 'crdt-gap'
  | 'awareness'

/**
 * Событие потока.
 *
 * Сигнальные виды несут только `kind` — клиент по ним перечитывает ленту.
 * Правки и присутствие (DEV-1972) приезжают телом: правку нельзя заменить
 * сигналом «перечитай» — перечитанная лента отдаст готовый текст и потеряет
 * то, ради чего всё затевалось.
 */
export interface MeetingEvent {
  kind: MeetingEventKind
  noteId?: string
  /** Номер правки в журнале заметки: по разрыву виден пропуск */
  seq?: number
  actorId?: string
  /** Тело правки или присутствия; на проводе — base64 */
  payload?: string
}

interface MeetingStreamOptions {
  signal: AbortSignal
  onEvent: (event: MeetingEvent) => void
}

/**
 * Поток событий одной встречи. Живёт, пока открыт мит-режим.
 *
 * Обрыв тут — обычное дело: выкат сервиса, спящий ноутбук, метро. Поэтому
 * функция просто заканчивается, а переподключение и догон пропущенного —
 * забота стора: только он знает, что именно перечитывать.
 */
/**
 * Поток закрыт насовсем: встречу разобрали либо человека в ней больше нет.
 *
 * Отдельный тип, а не код в тексте ошибки: обрыв связи лечится повтором, а
 * этот отказ — нет, и путать их значит долбить сервер по кругу.
 */
export class MeetingStreamClosed extends Error {}

export const streamMeeting = async (
  sessionId: string,
  options: MeetingStreamOptions,
): Promise<void> => {
  const response = await fetch(`${baseURL}/api/v1/tracker/plan/meeting/${sessionId}/stream`, {
    headers: { Authorization: `Bearer ${await ensureFreshToken()}` },
    signal: options.signal,
  })

  // Отказ навсегда: встречу разобрали (409) или человека в ней больше нет
  // (404). И то и другое повтором не лечится — отличаем от обрыва связи,
  // иначе цикл будет стучаться в закрытую дверь каждые пять секунд (DEV-2142).
  if (response.status === 404 || response.status === 409) {
    throw new MeetingStreamClosed()
  }

  if (!response.ok || !response.body) {
    throw new Error(`meeting stream: ${response.status}`)
  }

  await readNdjson<MeetingEvent & { note_id?: string; actor_id?: string }>(
    response.body,
    (event) => {
      options.onEvent({
        kind: event.kind ?? 'ping',
        // Ключи на проводе snake_case, как во всём JSON трекера.
        noteId: event.note_id,
        actorId: event.actor_id,
        seq: event.seq,
        payload: event.payload,
      })
    },
  )
}
